/*
  cart.js (TMG theme, Dawn-derived)

  [2026-09-19 Sydney] Interaction polish (Astra brief CLAUDE-INTERACTION-POLISH-2026-09-18, Joel: "Direct on the live theme").
  - Kept: debounced quantity changes, the per-line loading overlay, the pointer lock on the item list while a
    change is in flight (one change at a time), Shopify's quantity-limit message, section re-rendering and
    the focus hand-back after a successful change.
  - A change or removal whose reply is lost may still have reached Shopify, and cart lines are addressed by
    position, so a blind retry could change the wrong item. Instead the cart is read back and redrawn from
    the server. If even that fails, the row stays exactly as it was, with a message on the line, and the
    next change first refreshes the cart rather than sending a mutation.
  - Focus returns to the control that was used when a change fails, instead of being left on the page.
  - The cartUpdate refetch that follows an add swapped the drawer rows out from under keyboard focus
    (focus fell to <body>, outside the modal); focus now stays on the same position in the new rows.
  - Screen-reader status: the drawer's live region now says the new subtotal (it was always empty).
  - Error text from Shopify is written as text, not HTML. The card quick-add reset no longer throws on a
    card button without a sold-out label.
  Rollback copy of the previous live file: C:/GIT/chief-of-staff/outputs/tmg-home-v4/interaction-fixes/rollback/cart.js.live

  [2026-09-25 23:55 AEST] Honest pending state for quantity changes (TMG fix list #5, 25 Sep audit: after +, the
  subtotal stayed stale for ~1.4s with no cue and Checkout stayed live). From the moment a quantity changes until
  /cart/change answers (or the cart is read back), the cart's totals carry aria-busy="true" and an "Updating..."
  note, the subtotal dims, and Checkout is disabled; all of it clears on the reply. Styling lives in the shared
  layer assets/tmg-interactions.css (.tmg-cart-updating). Rollback: chief-of-staff
  outputs/tmg-fixes-2026-09-25/baseline/assets/cart.js
*/
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
    // Show the pending state at once, not after the 300ms debounce (fix #5). Quantity inputs only.
    this.addEventListener('change', (event) => {
      if (event.target && event.target.matches && event.target.matches('input[name="updates[]"], .quantity__input')) this.setPending(true);
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (!sourceQty) return;
        // The swap replaces the control a keyboard user may be on (the drawer has just taken focus after
        // an add); put focus back on the same position in the new markup instead of dropping it to <body>.
        const path = this.focusPath();
        this.innerHTML = sourceQty.innerHTML;
        if (path) this.refocus(path);
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    // An earlier change had an unknown outcome and the cart couldn't be read back: refresh first, change nothing.
    if (this.needsReconcile) {
      this.reconcile(line, document.activeElement);
      return;
    }

    const usedControl = document.activeElement;
    this.setPending(true);
    this.clearCartErrors(line);
    this.enableLoading(line);
    this.querySelectorAll('.quantity__button').forEach((button) => button.classList.add('disabled'));

    document.querySelectorAll('.card--product .card__add-to-cart button[name="add"]').forEach(button => {
      button.setAttribute('aria-disabled', false);
      button.querySelector('span')?.classList.remove('hidden');
      button.querySelector('.sold-out-message')?.classList.add('hidden');
    });
    
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');
        if (parsedState.errors || (parsedState.status && !parsedState.items)) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          // 'bad_request' (no such line) means the lines have moved since this cart was drawn.
          if (parsedState.status === 'bad_request' || parsedState.status === 400) {
            this.reconcile(line, usedControl);
            return;
          }
          this.updateLiveRegions(line, parsedState.errors || parsedState.description || parsedState.message || window.cartStrings.error);
          this.restoreFocus(usedControl);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        } else if (!cartDrawerWrapper) {
          // Cart page, row removed: land on the next item rather than the page body.
          this.focusSurvivor();
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
      }).catch((e) => {
        // No readable reply (connection dropped, server error): the change may or may not have happened.
        console.error(e);
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.querySelectorAll('.quantity__button').forEach((button) => button.classList.remove('disabled'));
        this.reconcile(line, usedControl);
      })
      .finally(() => {
        this.querySelectorAll('.quantity__button').forEach((button) => button.classList.remove('disabled'));
        this.disableLoading(line);
        if (!this.reconciling) this.setPending(false);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus') || this.lineItemStatusElement;
    this.lineItemStatusElement?.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    // The cart page renders its own status text; the drawer's region was always empty, so say the subtotal.
    if (cartStatus.id === 'CartDrawer-LiveRegionText') {
      const subtotal = document.querySelector('#CartDrawer .totals__subtotal-value')?.textContent.trim();
      cartStatus.textContent = message || (subtotal ? `New subtotal: ${subtotal}` : '');
    }
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /* ---------- unknown outcome: read the cart back instead of retrying ---------- */

  reconcile(line, usedControl) {
    const sections = this.getSectionsToRender();
    const sectionIds = [...new Set(sections.map((section) => section.section))];
    // No Accept header: with 'application/json' Shopify answers /cart?sections= with the cart, not the sections.
    const read = (url) => fetch(url, { cache: 'no-store' }).then((response) => {
      if (!response.ok) throw new Error(`${url} ${response.status}`);
      return response.json();
    });

    this.reconciling = true;
    this.setPending(true);
    Promise.all([read(`${routes.cart_url}.js`), read(`${routes.cart_url}?sections=${sectionIds.join(',')}`)])
      .then(([cart, rendered]) => {
        this.needsReconcile = false;
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        this.classList.toggle('is-empty', cart.item_count === 0);
        if (cartFooter) cartFooter.classList.toggle('is-empty', cart.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', cart.item_count === 0);

        sections.forEach((section) => {
          const html = rendered[section.section];
          const host = document.getElementById(section.id);
          if (!html || !host) return;
          const target = host.querySelector(section.selector) || host;
          target.innerHTML = this.getSectionInnerHTML(html, section.selector);
        });

        this.reconciling = false;
        this.setPending(false);
        this.showCartError(this.strings().refreshed);
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
        if (!this.restoreFocus(usedControl)) this.focusSurvivor();
      })
      .catch((e) => {
        console.error(e);
        // Can't reach the cart at all: leave every row as it is, say so on the line, refresh before the next change.
        this.needsReconcile = true;
        this.reconciling = false;
        this.setPending(false);
        this.disableLoading(line);
        this.showCartError(this.strings().offline, line);
        this.restoreFocus(usedControl);
      });
  }

  /* ---------- pending state while a quantity change is in flight (fix #5) ---------- */

  pendingRoot() {
    // The drawer re-render replaces this element, so resolve the root that survives: the <cart-drawer> host,
    // or the cart page's footer section.
    if (!this.pendingHost || !this.pendingHost.isConnected) {
      this.pendingHost = this.closest('cart-drawer') || document.getElementById('main-cart-footer');
    }
    return this.pendingHost;
  }

  setPending(on) {
    const root = this.pendingRoot();
    if (!root) return;
    root.classList.toggle('tmg-cart-updating', on);
    this.setAttribute('aria-busy', on ? 'true' : 'false');
    root.querySelectorAll('.totals').forEach((totals) => {
      totals.setAttribute('aria-busy', on ? 'true' : 'false');
      let note = totals.querySelector('.tmg-cart-pending');
      if (on && !note) {
        note = document.createElement('span');
        note.className = 'tmg-cart-pending';
        note.textContent = 'Updating…';
        totals.appendChild(note);
      } else if (!on && note) {
        note.remove();
      }
    });
    root.querySelectorAll('button[name="checkout"]').forEach((button) => {
      if (on && !button.disabled) {
        button.disabled = true;
        button.dataset.tmgPending = '1';
      } else if (!on && button.dataset.tmgPending) {
        button.disabled = false;
        delete button.dataset.tmgPending;
      }
    });
  }

  strings() {
    return {
      refreshed: "We couldn't confirm that change, so your cart has been refreshed. Please check it before changing it again.",
      offline: "We couldn't update your cart. Check your connection, then try again. Your cart will refresh first.",
    };
  }

  showCartError(message, line) {
    const lineItemError = line && (document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`));
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
      return;
    }
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = message;
  }

  clearCartErrors(line) {
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    const text = lineItemError && lineItemError.querySelector('.cart-item__error-text');
    if (text) text.textContent = '';
  }

  focusPath() {
    let node = document.activeElement;
    if (!node || !this.contains(node) || node === this) return null;
    const path = [];
    while (node && node !== this) {
      path.unshift(Array.prototype.indexOf.call(node.parentNode.children, node));
      node = node.parentNode;
    }
    return path;
  }

  refocus(path) {
    let node = this;
    for (const index of path) {
      node = node && node.children[index];
    }
    if (node && typeof node.focus === 'function') node.focus();
    if (document.activeElement === node) return;
    const dialog = this.closest('.drawer__inner');
    if (dialog) dialog.focus();
  }

  restoreFocus(element) {
    if (!element || element === document.body || !element.isConnected) return false;
    element.focus();
    return document.activeElement === element;
  }

  focusSurvivor() {
    const target = document.querySelector('.cart-item .cart-item__name') || document.querySelector('main h1');
    if (!target) return;
    if (!target.matches('a, button, input, [tabindex]')) target.setAttribute('tabindex', '-1');
    target.focus();
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));
    

    document.activeElement.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();

      this.addEventListener('change', debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER))
      }
  });
};